"use client";

import { useEffect, useState, type CSSProperties } from "react";
import type { ParkItem, ParkOption } from "@/common/interface/park";
import type { OpcItem, OpcProduct } from "@/common/interface/opc";
import { cityCover } from "@/common/parks/cityCover";
import { subscribeUser, login, logout, requireLogin, getCurrentUser, type PortalUserInfo } from "@/common/portal/portalOrder";
import { showToast } from "@/common/portal/toast";

export const go = (path: string) => {
  window.location.href = path;
};

/** 返回上一页；无历史记录时兜底回首页 */
export const goBack = () => {
  if (typeof window !== "undefined" && window.history.length > 1) window.history.back();
  else go("/");
};

/** 登录守卫跳转：校验登录态，未登录先弹登录框，登录后再跳转目标页 */
export const goWithLogin = (path: string) => {
  requireLogin(() => go(path), showToast);
};

const links = [
  ["找园区", "/cities"],
  ["找政策", "/policies"],
  ["找服务", "/growth"],
  ["找销路", "/sales"],
  ["找投资", "/investment"],
  ["找活动", "/venture"],
  ["找 OPC", "/opcs"],
] as const;

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

export const icons = {
  building: (
    <svg {...svgProps}>
      <path d="M4 21V4a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v17" />
      <path d="M15 9h4a1 1 0 0 1 1 1v11" />
      <path d="M3 21h18" />
      <path d="M7 7h4M7 11h4M7 15h4" />
    </svg>
  ),
  sparkle: (
    <svg {...svgProps}>
      <path d="M12 3l1.7 5.1L19 10l-5.3 1.9L12 17l-1.7-5.1L5 10l5.3-1.9z" />
      <path d="M18.5 14.5l.6 1.9 1.9.6-1.9.6-.6 1.9-.6-1.9-1.9-.6 1.9-.6z" />
    </svg>
  ),
  share: (
    <svg {...svgProps}>
      <circle cx="6" cy="12" r="2.2" />
      <circle cx="17" cy="6" r="2.2" />
      <circle cx="17" cy="18" r="2.2" />
      <path d="M8 11l6.8-3.6M8 13l6.8 3.6" />
    </svg>
  ),
  clipboard: (
    <svg {...svgProps}>
      <path d="M8 5H7a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-1" />
      <path d="M9 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1H9z" />
      <path d="M9 11h6M9 15h4" />
    </svg>
  ),
  menu: (
    <svg {...svgProps}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  ),
  close: (
    <svg {...svgProps}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
  box: (
    <svg {...svgProps}>
      <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" />
      <path d="M4 7.5l8 4.5 8-4.5" />
      <path d="M12 12v9" />
    </svg>
  ),
} as const;

export function Header({ active }: { active?: string }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState<PortalUserInfo>({ hasLogin: false });
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // 顶部导航：置顶时透明、下拉后加白底。Header 渲染于每个页面，故在此挂载以覆盖全域
  useEffect(() => {
    const onScroll = () => {
      document.documentElement.classList.toggle("scrolled", window.scrollY > 8);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.documentElement.classList.remove("scrolled");
    };
  }, []);

  // 订阅百度智能云门户登录态（首次自动拉取，登录/退出后自动刷新）
  useEffect(() => subscribeUser(setUser), []);

  // 点击下拉外部时收起用户菜单
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".user-menu")) setUserMenuOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [userMenuOpen]);

  return (
    <header>
      <button className="brand" onClick={() => go("/")} aria-label="返回 OPC 生态平台首页">
        <img className="brand-logo" src="https://bce.bdstatic.com/file-upload-skill/opc/logo/baidu-cloud-logo.png" alt="百度智能云" />
        <i />
        <em>OPC 生态平台</em>
      </button>
      <nav className={menuOpen ? "open" : undefined} aria-label="主导航">
        {links.map(([label, path]) => (
          <button className={active === label ? "active" : ""} key={path} onClick={() => go(path)}>
            {label}
          </button>
        ))}
      </nav>
      <div className="actions">
        {user.hasLogin ? (
          <div className={userMenuOpen ? "user-menu open" : "user-menu"}>
            <button
              type="button"
              className="user-trigger"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              onClick={() => setUserMenuOpen((open) => !open)}
            >
              <span className="user-name" title={user.displayName || "已登录"}>
                {user.displayName || "已登录"}
              </span>
              <svg className="caret" viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="user-dropdown" role="menu">
              <button type="button" role="menuitem" onClick={() => logout()}>
                退出登录
              </button>
            </div>
          </div>
        ) : (
          <button className="login-cta" onClick={() => login()}>
            登录 / 注册
          </button>
        )}
        <button
          className="menu"
          aria-label={menuOpen ? "关闭导航" : "打开导航"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? icons.close : icons.menu}
        </button>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer>
      <div className="footer-main">
        <div className="brand footer-brand">
          <img className="brand-logo" src="https://bce.bdstatic.com/file-upload-skill/opc/logo/baidu-cloud-logo-white.png" alt="百度智能云" /><i /><em>OPC 生态平台</em>
        </div>
        <p>连接一人企业与城市园区、产业项目和专业经营资源。</p>
      </div>
      <div className="footer-links">
        <div>
          <b>平台服务</b>
          {links.map(([label, path]) => <button key={path} onClick={() => go(path)}>{label}</button>)}
        </div>
        <div>
          <b>入驻服务</b>
          <button onClick={() => goWithLogin("/join-opc")}>OPC 入驻</button>
          <button onClick={() => goWithLogin("/join-park")}>园区 / 机构入驻</button>
        </div>
        <div>
          <b>服务说明</b>
          <span>政策、权益、名额和活动结果以发布机构的正式规则及最终审核结果为准。入驻资料由平台业务人员核验处理。</span>
        </div>
      </div>
      <div className="copyright">
        <p>
          <span>© 2026 Baidu</span>
          <a href="#">使用百度前必读</a>
          <span>增值电信业务经营许可证：B1.B2-20100266</span>
          <span>京ICP证030173号</span>
          <span>域名注册服务机构许可：京D3-20210001</span>
          <span>代理域名注册服务机构：商务中国 新网数码 江苏邦宁 西部数码</span>
        </p>
        <p>
          <span>京公网安备11000002000001号</span>
          <a href="#">隐私政策</a>
        </p>
      </div>
    </footer>
  );
}

const Field = ({ label, placeholder, area = false, name, type, required }: { label: string; placeholder: string; area?: boolean; name?: string; type?: string; required?: boolean }) => (
  <label>
    {label}
    {area
      ? <textarea rows={4} placeholder={placeholder} name={name} required={required} />
      : <input placeholder={placeholder} name={name} type={type} required={required} />}
  </label>
);

/** 入驻表单落库：读取 FormData → POST /api/submissions。返回是否成功 */
async function postSubmission(type: string, form: HTMLFormElement): Promise<boolean> {
  const payload: Record<string, string | string[]> = {};
  for (const [key, value] of new FormData(form).entries()) {
    if (typeof value !== "string") continue;
    const prev = payload[key];
    if (prev === undefined) payload[key] = value;
    else if (Array.isArray(prev)) prev.push(value);
    else payload[key] = [prev, value];
  }
  try {
    // 附带当前登录账号（仅运营参考，用于识别提交人；后端不作为可信身份校验）
    const user = getCurrentUser();
    const account = user.hasLogin
      ? { accountId: user.accountId ?? "", displayName: user.displayName ?? "" }
      : undefined;
    const res = await fetch("/api/submissions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type, payload, account }),
    });
    const data = await res.json();
    return !!data?.success;
  }
  catch {
    return false;
  }
}

/**
 * 提交成功弹窗：页面内弹窗展示成功话术，点击「确定」执行 onConfirm（默认返回上一页）。
 */
function SubmitSuccessModal({ open, title, message, onConfirm }: { open: boolean; title: string; message: string; onConfirm?: () => void }) {
  if (!open) return null;

  return (
    <div className="submit-success-overlay" role="dialog" aria-modal="true" aria-label="提交成功">
      <div className="submit-success-modal">
        <div className="submit-success-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 12.5l2.5 2.5L16 9" />
          </svg>
        </div>
        <h2>{title}</h2>
        <p>{message}</p>
        <button className="form-submit" type="button" onClick={onConfirm ?? goBack}>
          确定
        </button>
      </div>
    </div>
  );
}

function ContactInquiryModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="inquiry-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="发起合作咨询">
      <div className="inquiry-modal" onClick={(event) => event.stopPropagation()}>
        <button className="inquiry-modal-close" aria-label="关闭" onClick={onClose}>×</button>
        <div className="inquiry-modal-head">
          <h2>发起合作咨询</h2>
          <p>填写联系方式与咨询内容，平台会转交给企业，企业将在 3 个工作日内回复。</p>
        </div>
        <form className="join-form" onSubmit={(event) => { event.preventDefault(); setSubmitted(true); }}>
          <div className="form-section">
            <div className="form-grid">
              <label>联系人姓名<input placeholder="请输入姓名" required /></label>
              <label>联系电话<input type="tel" placeholder="请输入手机号" required /></label>
              <label>联系邮箱<input type="email" placeholder="用于接收企业回复" required /></label>
              <Field label="公司 / 组织" placeholder="选填" />
            </div>
            <label>咨询内容<textarea rows={4} placeholder="简要说明你的项目需求、渠道资源或合作意向" required /></label>
          </div>
          <button className="form-submit" type="submit">提交咨询 →</button>
        </form>
      </div>
      <SubmitSuccessModal
        open={submitted}
        title="提交成功"
        message="咨询已提交，企业将在 3 个工作日内与你联系。"
        onConfirm={onClose}
      />
    </div>
  );
}

function JoinPage({ institution = false }: { institution?: boolean }) {
  const [submitted, setSubmitted] = useState(false);
  return (
    <div className="application-page">
      <Header />
      <main className="form-page">
        <div className="form-heading">
          <h1>{institution ? <>开放你的园区资源<br />连接更多 OPC</> : <>让你的产品与能力<br />被更多人看见</>}</h1>
          <p>{institution ? "提交园区、孵化器、投资机构或产业服务机构信息，审核通过后发布政策、空间、项目和活动。" : "提交企业主体、产品服务和合作需求，资料核验通过后连接园区、客户、投资和经营资源。"}</p>
        </div>
        <div className="form-layout">
          <form
            className="join-form"
            onSubmit={async (event) => {
              event.preventDefault();
              const form = event.currentTarget;
              const ok = await postSubmission(institution ? "join-park" : "join-opc", form);
              if (ok) {
                form.reset();
                setSubmitted(true);
              }
              else {
                alert("提交失败，请稍后重试或检查网络。");
              }
            }}
          >
            <div className="form-section">
              <h2>{institution ? "机构基本信息" : "企业基本信息"}</h2>
              <p>请填写真实、有效的信息，平台将用于身份核验。</p>
              <div className="form-grid">
                <Field name="orgName" label={institution ? "机构名称" : "企业名称"} placeholder={institution ? "请输入园区或机构全称" : "请输入企业全称"} required />
                <Field name="city" label="所在城市" placeholder="请选择城市" required />
                <Field name={institution ? "orgType" : "creditCode"} label={institution ? "机构类型" : "统一社会信用代码"} placeholder={institution ? "产业园区 / 孵化器 / 投资机构" : "请输入统一社会信用代码"} required />
                <Field name="website" label="官方网站" placeholder="选填" />
              </div>
            </div>
            <div className="form-section">
              <h2>{institution ? "开放资源" : "产品与服务"}</h2>
              <p>{institution ? "选择你愿意向 OPC 开放的资源类型。" : "用简洁的方式介绍你正在做什么，以及已经验证的能力。"}</p>
              <Field name={institution ? "focusTracks" : "productName"} label={institution ? "重点产业方向" : "产品 / 服务名称"} placeholder={institution ? "例如：AI 应用、智能制造、企业服务" : "例如：灵犀零售客服 Agent"} required />
              <Field name="intro" label={institution ? "机构简介" : "详细介绍"} placeholder={institution ? "介绍园区产业方向、服务能力与希望连接的 OPC 类型" : "包括适用场景、核心能力、服务方式和已完成的项目"} area required />
              {institution && <div className="check-grid">{["办公空间与工位", "创业扶持政策", "产业项目与场景", "投资与融资对接", "技术与算力服务", "活动与培训"].map((item) => <label className="check-box" key={item}><input type="checkbox" name="openResources" value={item} />{item}</label>)}</div>}
            </div>
            <div className="form-section">
              <h2>联系人信息</h2>
              <div className="form-grid">
                <Field name="contactName" label="联系人姓名" placeholder="请输入姓名" required /><Field name="contactPhone" label="联系电话" placeholder="请输入手机号" type="tel" required />
                <Field name="contactEmail" label="联系邮箱" placeholder="用于接收审核结果" type="email" required /><Field name="contactTitle" label="职务" placeholder="例如：负责人" />
              </div>
            </div>
            <label className="check"><input type="checkbox" required />我已阅读并同意平台入驻规则，确认提交的信息真实有效。</label>
            <button className="form-submit" type="submit">提交{institution ? "机构" : "入驻"}申请 →</button>
            <p className="form-note">提交后由平台业务人员进行人工核验，通常在 3 个工作日内联系你。</p>
          </form>
          <aside className="form-aside">
            <div className={`aside-card${institution ? " institution" : ""}`}>
              <span>{institution ? "机构入驻价值" : "入驻后你将获得"}</span>
              <h2>{institution ? <>把园区的资源<br />变成长期连接</> : <>一张完整的<br />企业资源名片</>}</h2>
              <ul>
                <li><b>{institution ? "持续招募 OPC" : "企业展示"}</b><small>让平台用户快速了解你的能力与资源</small></li>
                <li><b>{institution ? "发布园区动态" : "资源对接"}</b><small>获得项目、活动、园区和渠道合作机会</small></li>
                <li><b>云生态权益</b><small>认证后按活动规则申请百度智能云专属资源</small></li>
              </ul>
            </div>
            <p className="privacy">你的资料仅用于审核及资源对接，不会公开联系人手机号。</p>
          </aside>
        </div>
      </main>
      <Footer />
      <SubmitSuccessModal
        open={submitted}
        title="提交成功"
        message={institution ? "机构入驻申请已提交，平台业务人员将在 3 个工作日内完成核验并与你联系。" : "入驻申请已提交，平台业务人员将在 3 个工作日内完成资料核验并与你联系。"}
      />
    </div>
  );
}

export function ParkDetail({ park }: { park?: ParkItem | null }) {
  if (!park) {
    return (
      <div className="application-page">
        <Header active="找园区" />
        <main className="detail-page park-detail-page">
          <section className="park-detail-hero"><div className="park-hero-content">
            <h1>未找到该园区</h1>
            <p className="park-hero-description">园区信息可能已更新，请返回列表重新选择。</p>
            <button className="primary-action" onClick={() => go("/cities")}>返回找园区 →</button>
          </div></section>
        </main>
        <Footer />
      </div>
    );
  }
  const loc = [park.city && `${park.city}市`, park.district].filter(Boolean).join("");
  const badge = "✓ OPC 认证园区";
  const meta = [loc, ...(park.focusTracks || [])].filter(Boolean).join(" · ");
  const applyHref = `/park-apply/${park.slug}`;
  const benefits = park.benefits || [];
  const introParas = (park.intro || park.desc || "").split("\n").filter(Boolean);
  const steps = park.entrySteps || [];
  const requirements = park.entryRequirements || [];
  const amenities = park.amenities || [];
  const tips = park.realTips || [];
  const benefitCount = benefits.reduce((n, b) => n + (b.items?.length || 0), 0);
  return (
    <div className="application-page">
      <Header active="找园区" />
      <main className="detail-page park-detail-page">
        <section
          className="park-detail-hero"
          style={{ "--park-cover": `url('${cityCover(park.city)}')` } as CSSProperties}
        >
          <div className="park-hero-content">
            <h1>{park.name}</h1>
            <div className="park-hero-tagline">
              <span className="park-hero-badge">{badge}</span>
              <span className="park-hero-meta">{meta}</span>
            </div>
            {park.desc && <p className="park-hero-description">{park.desc}</p>}
            <div className="detail-stats park-hero-stats">
              {park.workstations ? <div><b>{park.workstations}</b><span>共享工位</span></div> : null}
              {park.totalArea ? <div><b>{park.totalArea}</b><span>园区面积</span></div> : null}
              {park.focusTracks?.length ? <div><b>{park.focusTracks.length}</b><span>产业方向</span></div> : null}
              {benefitCount ? <div><b>{benefitCount}</b><span>入驻权益</span></div> : null}
            </div>
            <button className="primary-action" onClick={() => goWithLogin(applyHref)}>申请入驻园区 →</button>
          </div>
        </section>
        <div className="detail-columns park-detail-columns">
          <div className="park-detail-main">
            {introParas.length ? (
              <section className="detail-block park-intro-block"><h2>园区介绍</h2>
                {introParas.map((p, i) => <p key={i}>{p}</p>)}
              </section>
            ) : null}
            {benefits.length ? (
              <section className="detail-block"><h2>入驻政策与权益</h2>
                {benefits.map((b) => (
                  <div className="park-benefit" key={b.key || b.title}>
                    <h3>{b.title}</h3>
                    {b.summary && <p className="park-benefit-summary">{b.summary}</p>}
                    {b.items?.length ? (
                      <ul className="park-grid-points">{b.items.map((it, i) => <li key={i}>{it}</li>)}</ul>
                    ) : null}
                  </div>
                ))}
              </section>
            ) : null}
            {park.focusTracks?.length ? (
              <section className="detail-block"><h2>产业方向</h2>
                <div className="profile-tags">{park.focusTracks.map((t) => <span key={t}>{t}</span>)}</div>
              </section>
            ) : null}
            {steps.length || requirements.length || park.entryDuration ? (
              <section className="detail-block"><h2>入驻流程与条件</h2>
                {steps.length ? (
                  <ol className="park-steps">{steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
                ) : null}
                {park.entryDuration && <p className="park-benefit-summary">办理周期：{park.entryDuration}</p>}
                {requirements.length ? (
                  <div className="park-benefit"><h3>入驻条件</h3>
                    <ul className="park-grid-points">{requirements.map((r, i) => <li key={i}>{r}</li>)}</ul>
                  </div>
                ) : null}
              </section>
            ) : null}
            {amenities.length ? (
              <section className="detail-block"><h2>园区配套</h2>
                <div className="profile-tags">{amenities.map((a) => <span key={a}>{a}</span>)}</div>
              </section>
            ) : null}
            {tips.length ? (
              <section className="detail-block"><h2>入驻前须了解</h2>
                <ul className="park-grid-points">{tips.map((t, i) => <li key={i}>{t}</li>)}</ul>
              </section>
            ) : null}
          </div>
          <aside className="detail-side">
            <div className="contact-card park-location-card">
              <h3>园区位置</h3>
              <p className="park-location-addr">{park.address || loc || park.city}</p>
              {park.transit && <small>交通：{park.transit}</small>}
            </div>
            {park.operator && <div className="contact-card"><h3>运营主体</h3><p>{park.operator}</p></div>}
            {park.totalArea && <div className="contact-card"><h3>园区规模</h3><p>可用面积约 {park.totalArea}</p></div>}
            <div className="contact-card">
              <h3>入驻园区</h3>
              <p>提交入驻意向，园区将在 3 个工作日内联系你。</p>
              <button className="primary-action" onClick={() => goWithLogin(applyHref)}>申请入驻园区 →</button>
              <small>园区政策以运营方最终确认为准，平台信息定期核校更新。</small>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function DemandPublishPage({ variant }: { variant: "opc" | "purchase" }) {
  const opc = variant === "opc";
  const [submitted, setSubmitted] = useState(false);
  const tags = opc
    ? ["找客户 / 客户线索", "渠道代理与联合推广", "投资 / 融资与产业合作", "项目协作与联合交付"]
    : ["产品或服务采购", "业务场景验证与项目试点", "专业服务和技术交付", "渠道伙伴及解决方案招募"];
  return (
    <div className="application-page">
      <Header active="找销路" />
      <main className="form-page">
        <div className="form-heading">
          <h1>{opc ? <>发布经营需求<br />让平台为你匹配资源</> : <>发布采购需求<br />找到合适的 OPC 供给</>}</h1>
          <p>{opc
            ? "发布找客户、找渠道、找投资、联合交付及产品推广等经营需求，平台核验后推荐适合的企业、园区和生态资源。"
            : "发布产品采购、项目试点、服务采购和合作伙伴招募需求，平台核验后匹配具备相应能力的 OPC。"}</p>
        </div>
        <div className="form-layout">
          <form className="join-form" onSubmit={(event) => { event.preventDefault(); setSubmitted(true); }}>
            <div className="form-section">
              <h2>{opc ? "需求方信息" : "采购方信息"}</h2>
              <p>请填写真实、有效的信息，平台将用于身份核验与资源匹配。</p>
              <div className="form-grid">
                <Field label={opc ? "企业 / 团队名称" : "采购方名称"} placeholder={opc ? "请输入企业或团队全称" : "请输入企业 / 园区 / 机构全称"} required />
                <Field label="所在城市" placeholder="请输入所在城市" required />
                <Field label={opc ? "所属行业" : "机构类型"} placeholder={opc ? "例如：企业服务、智能制造" : "企业 / 园区 / 生态机构"} required />
                <Field label="官方网站" placeholder="选填" />
              </div>
            </div>
            <div className="form-section">
              <h2>{opc ? "需求内容" : "采购内容"}</h2>
              <p>{opc ? "描述你的产品、能力或项目，以及希望对接的资源类型。" : "描述你要采购的产品或服务，以及项目背景。"}</p>
              <Field label={opc ? "需求标题" : "采购标题"} placeholder={opc ? "例如：寻找华东区渠道代理" : "例如：采购零售门店客服智能体"} required />
              <Field label="详细说明" placeholder={opc ? "包括产品 / 能力介绍、目标客户、合作方式及预期" : "包括采购范围、场景要求、预算区间及交付时间"} area required />
              <div className="check-grid">{tags.map((item) => <label className="check-box" key={item}><input type="checkbox" />{item}</label>)}</div>
            </div>
            <div className="form-section">
              <h2>联系人信息</h2>
              <div className="form-grid">
                <Field label="联系人姓名" placeholder="请输入姓名" required /><Field label="联系电话" placeholder="请输入手机号" type="tel" required />
                <Field label="联系邮箱" placeholder="用于接收匹配反馈" type="email" required /><Field label="职务" placeholder="例如：负责人" />
              </div>
            </div>
            <label className="check"><input type="checkbox" required />我确认提交的信息真实有效，并同意平台用于资源匹配与对接。</label>
            <button className="form-submit" type="submit">提交{opc ? "经营" : "采购"}需求 →</button>
            <p className="form-note">提交后由平台业务人员人工核验，通常在 3 个工作日内联系你。</p>
          </form>
          <aside className="form-aside">
            <div className="aside-card">
              <span>发布后你将获得</span>
              <h2>{opc ? <>把经营需求<br />交给平台匹配</> : <>把采购需求<br />精准触达 OPC</>}</h2>
              <ul>
                <li><b>{opc ? "资源匹配" : "供给匹配"}</b><small>{opc ? "对接园区、客户、投资与渠道资源" : "匹配具备相应能力的 OPC 产品与服务"}</small></li>
                <li><b>人工跟进</b><small>一期由平台业务人员人工核验并推荐</small></li>
                <li><b>云生态权益</b><small>按活动规则申请百度智能云专属资源</small></li>
              </ul>
            </div>
            <p className="privacy">你的资料仅用于审核及资源对接，不会公开联系人手机号。</p>
          </aside>
        </div>
      </main>
      <Footer />
      <SubmitSuccessModal
        open={submitted}
        title="提交成功"
        message={opc ? "经营需求已提交，平台会在 3 个工作日内联系你。" : "采购需求已提交，平台会在 3 个工作日内联系你。"}
      />
    </div>
  );
}

export function ParkApplyPage({ options = [], preselect = "" }: { options?: ParkOption[]; preselect?: string }) {
  const preset = preselect ? options.find((o) => o.slug === preselect) : undefined;
  const [province, setProvince] = useState(preset?.province ?? "");
  const [city, setCity] = useState(preset?.city ?? "");
  const [slug, setSlug] = useState(preset?.slug ?? "");
  const [submitted, setSubmitted] = useState(false);

  const provinces = Array.from(new Set(options.map((o) => o.province).filter(Boolean)));
  const cities = Array.from(new Set(options.filter((o) => o.province === province).map((o) => o.city).filter(Boolean)));
  const parks = options.filter((o) => o.province === province && o.city === city);
  const selected = options.find((o) => o.slug === slug);

  const onProvince = (value: string) => { setProvince(value); setCity(""); setSlug(""); };
  const onCity = (value: string) => { setCity(value); setSlug(""); };

  return (
    <div className="application-page">
      <Header active="找园区" />
      <main className="form-page">
        <div className="form-heading">
          <h1>OPC 园区入驻申请</h1>
          <p>选择目标园区并提交企业与入驻需求，园区招商顾问核验信息后将在 3 个工作日内联系你。</p>
        </div>
        <div className="form-layout">
          <form
            className="join-form"
            onSubmit={async (event) => {
              event.preventDefault();
              const form = event.currentTarget;
              const ok = await postSubmission("park-apply", form);
              if (ok) {
                form.reset();
                setSubmitted(true);
              }
              else {
                alert("提交失败，请稍后重试或检查网络。");
              }
            }}
          >
            <div className="form-section">
              <h2>选择意向园区</h2>
              <p>按省 / 市 / 园区逐级选择你希望入驻的园区。</p>
              <div className="form-grid">
                <label>意向省份
                  <select name="province" value={province} onChange={(e) => onProvince(e.target.value)} required>
                    <option value="">请选择省份</option>
                    {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
                <label>意向城市
                  <select name="parkCity" value={city} onChange={(e) => onCity(e.target.value)} disabled={!province} required>
                    <option value="">{province ? "请选择城市" : "请先选择省份"}</option>
                    {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </div>
              <label>意向园区
                <select name="parkName" value={slug} onChange={(e) => setSlug(e.target.value)} disabled={!city} required>
                  <option value="">{city ? "请选择园区" : "请先选择城市"}</option>
                  {parks.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                </select>
              </label>
            </div>
            <div className="form-section">
              <h2>企业基本信息</h2>
              <p>用于确认申请主体与园区产业方向的匹配情况。</p>
              <div className="form-grid">
                <Field name="orgName" label="企业名称" placeholder="请输入企业全称" required />
                <Field name="creditCode" label="统一社会信用代码" placeholder="请输入统一社会信用代码" required />
                <Field name="city" label="所在城市" placeholder="请输入企业所在城市" required />
                <Field name="teamSize" label="团队人数" placeholder="例如：5 人" />
              </div>
            </div>
            <div className="form-section">
              <h2>入驻需求</h2>
              <p>说明业务方向和具体需求，便于园区提前准备对接方案。</p>
              <Field name="bizIntro" label="产品 / 业务简介" placeholder="介绍产品方向、目标客户和当前发展阶段" area required />
              <div className="form-grid">
                <Field name="entryTime" label="计划入驻时间" placeholder="例如：2026 年 9 月" />
                <Field name="workstations" label="预计工位数量" placeholder="例如：4 个工位" />
              </div>
              <Field name="resourceNeeds" label="其他资源需求" placeholder="例如：算力支持、政策咨询、企业场景对接" area />
            </div>
            <div className="form-section">
              <h2>联系人信息</h2>
              <div className="form-grid">
                <Field name="contactName" label="联系人姓名" placeholder="请输入姓名" required />
                <Field name="contactPhone" label="联系电话" placeholder="请输入手机号" type="tel" required />
                <Field name="contactEmail" label="联系邮箱" placeholder="用于接收入驻反馈" type="email" required />
                <Field name="contactTitle" label="职务" placeholder="例如：创始人" />
              </div>
            </div>
            <label className="check"><input type="checkbox" required />我确认提交的信息真实有效，并同意平台将信息转交园区用于入驻对接。</label>
            <button className="form-submit" type="submit">提交入驻意向 →</button>
            <p className="form-note">提交后由园区招商顾问人工核验，通常在 3 个工作日内联系你。</p>
          </form>
          <aside className="form-aside">
            <div className="aside-card">
              <span>本次申请园区</span>
              <h2>{selected ? selected.name : "尚未选择园区"}</h2>
              {selected
                ? <p className="park-owner">{[selected.province, selected.city].filter(Boolean).join(" · ")}</p>
                : <p className="park-owner">请在左侧按省 / 市 / 园区选择意向园区。</p>}
              <ul>
                <li><b>办公空间</b><small>灵活工位与企业注册地址支持</small></li>
                <li><b>算力资源</b><small>算力补贴与 AI 工具配套支持</small></li>
                <li><b>产业连接</b><small>政策咨询、企业场景和产业活动对接</small></li>
              </ul>
            </div>
            <p className="privacy">联系人信息仅用于本次园区入驻审核与对接。</p>
          </aside>
        </div>
      </main>
      <Footer />
      <SubmitSuccessModal
        open={submitted}
        title="提交成功"
        message="入驻意向已提交，园区招商顾问将在 3 个工作日内完成核验并与你联系。"
      />
    </div>
  );
}

/** 详情数据缺失时的兜底：不 500，引导回列表 */
function MissingDetail({ title }: { title: string }) {
  return (
    <div className="application-page">
      <Header active="找 OPC" />
      <main className="detail-page">
        <section className="detail-block">
          <h2>{title}</h2>
          <p>该信息不存在或已下架，可返回列表查看其他 OPC 与产品。</p>
          <button className="primary-action" onClick={() => go("/opcs")}>返回找 OPC →</button>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export function OpcDetail({ opc, products = [] }: { opc: OpcItem | null; products?: OpcProduct[] }) {
  if (!opc) {
    return <MissingDetail title="未找到该 OPC" />;
  }
  const tags = [opc.city, opc.industry, ...opc.tags].filter(Boolean);
  return (
    <div className="application-page">
      <Header active="找 OPC" />
      <main className="detail-page">
        <section className="opc-profile-hero">
          <div className="company-logo">{icons.building}</div>
          <div>
            <h1>{opc.name}</h1>
            <div className="opc-profile-meta">
              {opc.title ? <p>{opc.title}</p> : null}
            </div>
            {tags.length ? <div className="profile-tags">{tags.map((t) => <span key={t}>{t}</span>)}</div> : null}
          </div>
        </section>
        <div className="detail-columns">
          <div>
            <section className="detail-block">
              <h2>主体简介</h2>
              <p>{opc.intro}</p>
            </section>
            <section className="detail-block">
              <h2>产品与服务</h2>
              {products.length ? (
                <div className="support-grid">
                  {products.map((p) => (
                    <article key={p.slug}>
                      <i>{icons.sparkle}</i>
                      <h3>{p.name}</h3>
                      <p>{p.tagline}</p>
                      <button className="primary-action" onClick={() => go(`/product-detail/${p.id}`)}>查看产品详情 →</button>
                    </article>
                  ))}
                </div>
              ) : <p>暂无已核验的产品信息。</p>}
            </section>
          </div>
          <aside className="detail-side">
            <OpcContactCard />
            <div className="contact-card">
              <h3>公开链接</h3>
              {opc.links.length ? (
                <ul className="link-list">
                  {opc.links.map((l) => (
                    <li key={l.url}><a href={l.url} target="_blank" rel="noreferrer">{l.label} ↗</a></li>
                  ))}
                </ul>
              ) : <p>暂无公开链接。</p>}
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function ProductDetail({ product, owner = null }: { product: OpcProduct | null; owner?: OpcItem | null }) {
  if (!product) {
    return <MissingDetail title="未找到该产品" />;
  }
  const tags = [product.category, product.pricing, ...product.tags].filter(Boolean);
  return (
    <div className="application-page">
      <Header active="找 OPC" />
      <main className="detail-page">
        <section className="opc-profile-hero product-profile-hero">
          <div className="company-logo">{icons.sparkle}</div>
          <div>
            <h1>{product.name}</h1>
            <div className="opc-profile-meta">
              <p>{product.ownerName ? `开发者 ${product.ownerName}` : "开发者信息未公开"}</p>
            </div>
            {tags.length ? <div className="profile-tags">{tags.map((t) => <span key={t}>{t}</span>)}</div> : null}
          </div>
        </section>
        <div className="detail-columns">
          <div>
            <section className="detail-block">
              <h2>产品介绍</h2>
              <p>{product.tagline}</p>
            </section>
            {(product.website || product.launchDate || product.pricing) ? (
              <section className="detail-block">
                <h2>公开信息</h2>
                <ul className="link-list">
                  {product.website ? <li><a href={product.website} target="_blank" rel="noreferrer">产品官网 ↗</a></li> : null}
                  {product.launchDate ? <li>发布时间：{product.launchDate}</li> : null}
                  {product.pricing ? <li>定价方式：{product.pricing}</li> : null}
                </ul>
              </section>
            ) : null}
          </div>
          <aside className="detail-side">
            {owner ? (
              <div className="contact-card">
                <h3>所属 OPC</h3>
                <p>{owner.name}{owner.title ? ` · ${owner.title}` : ""}</p>
                <button className="primary-action" onClick={() => go(`/opc-detail/${owner.id}`)}>查看 OPC 详情 →</button>
              </div>
            ) : (
              <div className="contact-card">
                <h3>所属 OPC</h3>
                <p>该产品来自公开产品榜，榜单未公开开发者主体信息。</p>
              </div>
            )}
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function OpcContactCard() {
  const [inquiryOpen, setInquiryOpen] = useState(false);
  return (
    <>
      <div className="contact-card"><h3>联系这家企业</h3><p>如果你有项目需求、渠道资源或合作意向，可以向企业发起咨询。</p><button className="primary-action" onClick={() => setInquiryOpen(true)}>发起合作咨询 →</button><small>平台会转交你的联系信息，企业将在 3 个工作日内回复。</small></div>
      <ContactInquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} />
    </>
  );
}

export { JoinPage };
